import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  attemptMemberSignup,
  MemberSignupAlreadyClaimedError,
  MemberSignupCodeRequiredError,
} from "@/lib/member-signup";
import { requestMemberSetupCode } from "@/lib/auth.functions";
import { supabase } from "@/integrations/supabase/client";
import { m } from "@/paraglide/messages.js";

type AuthInlineProps = {
  heading?: string;
  description?: string;
  defaultMode?: "signin" | "signup";
};

export function AuthInline({ heading, description, defaultMode = "signin" }: AuthInlineProps) {
  const [mode, setMode] = useState<"signin" | "signup">(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [needsSetupCode, setNeedsSetupCode] = useState(false);
  const [busy, setBusy] = useState(false);

  const resolvedHeading = heading ?? m.auth_inline_default_heading();
  const resolvedDescription = description ?? m.auth_inline_default_desc();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const result = await attemptMemberSignup({
          email,
          password,
          fullName: name,
          emailRedirectTo: window.location.href,
          code: needsSetupCode ? setupCode : undefined,
        });
        toast.success(
          result.claimed ? m.auth_inline_toast_ready() : m.auth_inline_toast_created_verify(),
        );
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success(m.auth_inline_toast_signed_in());
      }
    } catch (e) {
      if (e instanceof MemberSignupCodeRequiredError) {
        setNeedsSetupCode(true);
        toast.success(m.auth_toast_setup_code_sent());
      } else if (e instanceof MemberSignupAlreadyClaimedError) {
        toast.error(m.auth_error_already_claimed(), { duration: 8000 });
      } else {
        toast.error((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const resendSetupCode = async () => {
    setBusy(true);
    try {
      await requestMemberSetupCode({ data: { email } });
      toast.success(m.auth_toast_setup_code_sent());
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6">
      <h3 className="font-display text-lg font-semibold">{resolvedHeading}</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {mode === "signup" && needsSetupCode ? m.auth_sub_signup_code() : resolvedDescription}
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3">
        {mode === "signup" && (
          <div>
            <Label htmlFor="auth-nm">{m.auth_label_full_name()}</Label>
            <Input id="auth-nm" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
        )}
        <div>
          <Label htmlFor="auth-em">{m.auth_label_email()}</Label>
          <Input
            id="auth-em"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="auth-pw">{m.auth_label_password()}</Label>
          <Input
            id="auth-pw"
            type="password"
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {mode === "signup" && needsSetupCode && (
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="auth-setup-code">{m.auth_label_setup_code()}</Label>
              <button
                type="button"
                onClick={() => void resendSetupCode()}
                disabled={busy}
                className="text-xs text-accent hover:underline"
              >
                {m.auth_btn_resend_setup_code()}
              </button>
            </div>
            <Input
              id="auth-setup-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground">
          {mode === "signup" ? m.auth_btn_create() : m.auth_btn_signin()}
        </Button>
      </form>

      <button
        type="button"
        onClick={() => {
          setMode(mode === "signup" ? "signin" : "signup");
          setNeedsSetupCode(false);
          setSetupCode("");
        }}
        className="mt-4 text-sm text-accent hover:underline"
      >
        {mode === "signup" ? m.auth_inline_have_account() : m.auth_inline_need_account()}
      </button>
    </div>
  );
}
